import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { Integration, CreateIntegrationDto, UpdateIntegrationDto, Integration_Types } from '../models/integration.models';

@Injectable({
  providedIn: 'root'
})
export class IntegrationService {
  
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) { }

  /**
   * Obtiene todas las integraciones
   */
  getIntegrations(): Observable<Integration[]> {
    return this.http.get<Integration[]>(`${this.apiUrl}integrations`);
  }

  /**
   * Obtiene integraciones por tipo
   */
  getIntegrationsByType(type: Integration_Types): Observable<Integration[]> {
    return this.http.get<Integration[]>(`${this.apiUrl}integrations`, {
      params: { type: type }
    });
  }

  /**
   * Obtiene todas las integraciones de métodos de pago (activas e inactivas)
   */
  getPaymentIntegrations(): Observable<Integration[]> {
    return this.http.get<Integration[]>(`${this.apiUrl}integrations`, {
      params: { 
        type: Integration_Types.PAYMENT_GATEWAY
      }
    });
  }

  /**
   * Obtiene una integración por ID
   */
  getIntegrationById(id: number): Observable<Integration> {
    return this.http.get<Integration>(`${this.apiUrl}integrations/${id}`);
  }

  /**
   * Crea una nueva integración
   */
  createIntegration(integration: CreateIntegrationDto): Observable<Integration> {
    return this.http.post<Integration>(`${this.apiUrl}integrations`, integration);
  }

  /**
   * Actualiza una integración existente
   */
  updateIntegration(id: number, integration: UpdateIntegrationDto): Observable<Integration> {
    return this.http.patch<Integration>(`${this.apiUrl}integrations/${id}`, integration);
  }

  /**
   * Elimina una integración
   */
  deleteIntegration(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}integrations/${id}`);
  }

  /**
   * Activa/desactiva una integración
   */
  toggleIntegrationStatus(id: number, isActive: boolean): Observable<Integration> {
    return this.http.patch<Integration>(`${this.apiUrl}integrations/${id}/status`, { isActive });
  }

  /**
   * Obtiene integraciones con paginación y filtros
   */
  getIntegrationsWithPagination(
    page: number = 0, 
    size: number = 20, 
    search?: string,
    type?: Integration_Types,
    isActive?: boolean
  ): Observable<{data: Integration[], total: number, page: number, size: number}> {
    let params: any = { page: page.toString(), size: size.toString() };
    
    if (search) params.search = search;
    if (type) params.type = type;
    if (isActive !== undefined) params.isActive = isActive.toString();
    
    return this.http.get<{data: Integration[], total: number, page: number, size: number}>(
      `${this.apiUrl}integrations/paginated`, 
      { params }
    );
  }

  /**
   * Busca integraciones por nombre o descripción
   */
  searchIntegrations(query: string): Observable<Integration[]> {
    return this.http.get<Integration[]>(`${this.apiUrl}integrations/search`, {
      params: { q: query }
    });
  }

  /**
   * Obtiene integraciones por proveedor
   */
  getIntegrationsByProvider(provider: string): Observable<Integration[]> {
    return this.http.get<Integration[]>(`${this.apiUrl}integrations`, {
      params: { provider: provider }
    });
  }

  /**
   * Obtiene todas las integraciones activas
   */
  getActiveIntegrations(): Observable<Integration[]> {
    return this.http.get<Integration[]>(`${this.apiUrl}integrations/active`);
  }

  /**
   * Obtiene integraciones del sistema vs personalizadas
   */
  getSystemIntegrations(): Observable<Integration[]> {
    return this.http.get<Integration[]>(`${this.apiUrl}integrations/system`);
  }

  getCustomIntegrations(): Observable<Integration[]> {
    return this.http.get<Integration[]>(`${this.apiUrl}integrations/custom`);
  }

  /**
   * Duplica una integración existente
   */
  duplicateIntegration(id: number, newName: string): Observable<Integration> {
    return this.http.post<Integration>(`${this.apiUrl}integrations/${id}/duplicate`, { 
      name: newName 
    });
  }

  /**
   * Testa la conectividad de una integración
   */
  testIntegrationConnection(id: number): Observable<{success: boolean, message: string, responseTime?: number}> {
    return this.http.post<{success: boolean, message: string, responseTime?: number}>(
      `${this.apiUrl}integrations/${id}/test`, {}
    );
  }

  /**
   * Obtiene estadísticas de uso de integraciones
   */
  getIntegrationStats(): Observable<{
    total: number,
    active: number,
    inactive: number,
    byType: {[key: string]: number},
    byProvider: {[key: string]: number}
  }> {
    return this.http.get<any>(`${this.apiUrl}integrations/stats`);
  }

  /**
   * Exporta configuraciones de integraciones
   */
  exportIntegrations(ids?: number[]): Observable<Blob> {
    let params: any = {};
    if (ids && ids.length > 0) {
      params.ids = ids.join(',');
    }
    
    return this.http.get(`${this.apiUrl}integrations/export`, {
      params,
      responseType: 'blob'
    });
  }

  /**
   * Importa configuraciones de integraciones
   */
  importIntegrations(file: File): Observable<{success: boolean, imported: number, errors?: string[]}> {
    const formData = new FormData();
    formData.append('file', file);
    
    return this.http.post<{success: boolean, imported: number, errors?: string[]}>(
      `${this.apiUrl}integrations/import`, 
      formData
    );
  }
}